import { useEffect, useState } from "react";
import { Modal, Form, Input, Select, message } from "antd";
import { createProject, updateProject, fetchUsers } from "../api/ProjectsAPI";

export default function AddEditProjectModal({ project, onClose, onSuccess }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const isEdit = !!project;

  useEffect(() => {
    if (isEdit) {
      form.setFieldsValue({
        title: project.title,
        description: project.description,
        status: project.status,
        progress: project.progress,
        collaboratorIds: project.collaborators?.map((c) => c.id) || [],
      });
    } else {
      form.resetFields();
    }
  }, [project, form, isEdit]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await fetchUsers();
        setUsers(res || []);
      } catch (e) {
        message.warning("Could not load users for collaborators");
      }
    };
    loadUsers();
  }, []);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      if (isEdit) {
        await updateProject(project.id, values);
        message.success("Project updated successfully");
      } else {
        await createProject(values);
        message.success("Project created successfully");
      }
      onSuccess();
      onClose();
    } catch (error) {
      message.error(`Failed to ${isEdit ? "update" : "create"} project`);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    { label: "Not Started", value: "NOT_STARTED" },
    { label: "Active", value: "ACTIVE" },
    { label: "On Hold", value: "ON_HOLD" },
    { label: "Archived", value: "ARCHIVED" },
    { label: "Completed", value: "COMPLETED" },
  ];

  const progressOptions = [
    { label: "Not Started", value: "NOT_STARTED" },
    { label: "Governeye Setup", value: "GOVERNEYE_SETUP" },
    { label: "Business Goals", value: "BUSINESS_GOALS" },
    { label: "DG Assessment", value: "DG_ASSESSMENT" },
    { label: "Data Strategy", value: "DATA_STRATEGY" },
    { label: "Use Case Book", value: "USE_CASE_BOOK" },
    { label: "UC Prioritization", value: "UC_PRIORITIZATION" },
    { label: "Data Catalog", value: "DATA_CATALOG" },
    { label: "Data Quality", value: "DATA_QUALITY" },
    { label: "Skeye Setup", value: "SKEYE_SETUP" },
    { label: "EDA", value: "EDA" },
    { label: "Implementation", value: "IMPLEMENTATION" },
    { label: "Dashboarding", value: "DASHBOARDING" },
    { label: "Predictive Analytics", value: "PREDICTIVE_ANALYTICS" },
  ];

  return (
    <Modal
      title={isEdit ? "Edit Project" : "New Project"}
      open={true}
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={loading}
      okText="Save"
      width={600}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: "Please input project title" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={3} />
        </Form.Item>

        {isEdit && (
          <>
            <Form.Item name="status" label="Status">
              <Select options={statusOptions} />
            </Form.Item>
            <Form.Item name="progress" label="Progress">
              <Select mode="multiple" options={progressOptions} />
            </Form.Item>
            <Form.Item name="collaboratorIds" label="Collaborators">
              <Select
                mode="multiple"
                placeholder="Select collaborators"
                options={users.map((u) => ({ label: u.username, value: u.id }))}
              />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
}
